import React, { Component } from 'react'
import { filter, first, includes, map, reverse, size, takeRight } from 'lodash'
import { produce } from 'immer'
import storageService from '../services/storage.service'
import geolocationService from '../services/geolocation.service'
import labelTraceApi, {
  LabelTraceCreateManyRequest
} from '../apis/labelTrace.api'
import appUtil from '../utils/app.util'
import ContentAside from '../components/ContentAside'
import ContentLayout from '../components/ContentLayout'
import ContentMain from '../components/ContentMain'
import LabelTraceForm from '../partials/LabelTraceForm'
import LabelTraceProgress from '../partials/LabelTraceProgress'
import LabelTraceStatus from '../partials/LabelTraceStatus'

interface LabelTraceScan {
  workOrderId: number
  lineNumber: string
  code: string
}

interface LabelTraceProps {}

interface LabelTraceState {
  scanned: number
  sent: number
  scans: LabelTraceScan[]
  latitude: number | null
  longitude: number | null
}

export default class LabelTrace
  extends Component<LabelTraceProps, LabelTraceState> {
  static config = {
    createCount: appUtil.isEnv('production') ? 250 : 20,
    processCount: 20
  }

  constructor (props: LabelTraceProps) {
    super(props)
    this.state = {
      scanned: 0,
      sent: 0,
      scans: [],
      latitude: null,
      longitude: null
    }
    this.handleScanCode = this.handleScanCode.bind(this)
  }

  get latestCodes (): string[] {
    const codes = map(this.state.scans, scan => scan.code)
    const { processCount } = LabelTrace.config
    const limit = takeRight(codes, processCount)
    return reverse(limit)
  }

  get status () {
    const { sent, scanned } = this.state
    const pending = scanned - sent
    return { pending, processed: sent }
  }

  get canCreate () {
    const { pending } = this.status
    const { createCount } = LabelTrace.config
    return (pending && (pending % createCount) === 0)
  }

  get createData () {
    const firstScan = first(this.state.scans)
    if (!firstScan) return null
    const { workOrderId, lineNumber } = firstScan
    const scans = filter(this.state.scans, { workOrderId, lineNumber })
    const codes = map(scans, scan => scan.code)
    return { workOrderId, lineNumber, codes }
  }

  listScans () {
    const scans = storageService.get('labelTraceScans') || []
    this.updateScans(scans)
    this.setScanned(scans)
  }

  updateScans (scans: LabelTraceScan[]) {
    storageService.set('labelTraceScans', scans)
    this.setState({ scans }, this.createLabelTraces)
  }

  setScanned (scans: LabelTraceScan[]) {
    const scanned = size(scans)
    this.setState({ scanned })
  }

  incrementScanned () {
    this.setState(state => {
      const scanned = state.scanned + 1
      return { scanned }
    })
  }

  pushScan (workOrderId: number, lineNumber: string, code: string) {
    const scans = produce(this.state.scans, scans => {
      scans.push({ workOrderId, lineNumber, code })
    })
    this.updateScans(scans)
    this.incrementScanned()
  }

  pullScans (workOrderId: number, lineNumber: string, codes: string[]) {
    const scans = filter(this.state.scans, scan => {
      const isWorkOrder = scan.workOrderId === workOrderId
      const isLineNumber = scan.lineNumber === lineNumber
      const hasCodes = includes(codes, scan.code)
      const canKeep = (!isWorkOrder || !isLineNumber) && !hasCodes
      return canKeep
    })
    this.updateScans(scans)
  }

  updateSent (codes: string[]) {
    this.setState(state => {
      const sent = state.sent + size(codes)
      return { sent }
    })
  }

  createLabelTraces () {
    if (!this.canCreate || !this.createData) return
    const { workOrderId, lineNumber, codes } = this.createData
    const { latitude, longitude } = this.state
    const payload: LabelTraceCreateManyRequest = {}
    if (workOrderId) payload.workOrder = workOrderId
    if (lineNumber) payload.lineNumber = lineNumber
    if (codes) payload.scans = codes
    if (latitude) payload.latitude = latitude
    if (longitude) payload.longitude = longitude
    labelTraceApi.createMany(payload, labelTraces => {
      if (!labelTraces) return
      this.pullScans(workOrderId, lineNumber, codes)
      this.updateSent(codes)
    })
  }

  handleScanCode (workOrderId: number, lineNumber: string, code: string) {
    this.pushScan(workOrderId, lineNumber, code)
  }

  readGeolocation () {
    geolocationService.access(coordinate => {
      if (!coordinate) return
      const { latitude, longitude } = coordinate
      this.setState({ latitude, longitude })
    })
  }

  componentDidMount () {
    this.listScans()
    this.readGeolocation()
  }

  render () {
    return (
      <ContentLayout>
        <ContentMain layout="oneThird">
          <LabelTraceForm onScan={this.handleScanCode} />
        </ContentMain>
        <ContentMain layout="oneThird">
          <LabelTraceStatus status={this.status} />
        </ContentMain>
        <ContentAside layout="oneThird">
          <LabelTraceProgress codes={this.latestCodes} />
        </ContentAside>
      </ContentLayout>
    )
  }
}
